import React from 'react';
import { Label } from '@/components/ui/label';
import StylishNewSelect from 'components/DesignSystems/New/StylishNewSelect';
import { formatOperationalPeriodLabel } from 'utils/iapFormsView';
import type { OperationalPeriod } from 'components/IAP/hooks/incidentHooks';

export type ApprovedFormsPeriodHeaderProps = {
  periods: OperationalPeriod[];
  selectedPeriodNumber: number;
  onPeriodChange: (periodNumber: number) => void;
};

/**
 * Period picker for Approved ICS Forms (current + historical, always
 * read-only).
 */
export const ApprovedFormsPeriodHeader: React.FC<
  ApprovedFormsPeriodHeaderProps
> = ({ periods, selectedPeriodNumber, onPeriodChange }) => {
  const sortedPeriods = [...periods].sort(
    (a, b) => (a.period_number ?? 0) - (b.period_number ?? 0)
  );

  const options = sortedPeriods.map((period) => ({
    value: String(period.period_number ?? 0),
    label: `${formatOperationalPeriodLabel(period.period_number ?? 0)} (read-only)`,
  }));

  const selected =
    options.find((o) => o.value === String(selectedPeriodNumber)) ??
    options.at(-1) ??
    null;

  return (
    <div
      className="tw:flex tw:flex-wrap tw:items-end tw:justify-between tw:gap-3 tw:border-b tw:border-border tw:bg-black tw:px-3 tw:py-2"
      data-testid="iap-forms-op-header"
    >
      <div className="tw:min-w-0">
        <Label className="tw:text-xs tw:font-medium tw:text-foreground">
          Operational period
        </Label>
        <p className="tw:m-0 tw:text-[11px] tw:text-muted-foreground">
          Viewing a past or current period (read-only)
        </p>
      </div>

      {options.length > 0 ? (
        <div className="tw:min-w-[12rem]" data-testid="iap-forms-op-select">
          <StylishNewSelect
            classes="secondary sml"
            options={options}
            value={selected}
            onChange={(option: { value: string } | null) => {
              if (!option) return;
              const n = Number.parseInt(option.value, 10);
              if (Number.isFinite(n)) {
                onPeriodChange(n);
              }
            }}
          />
        </div>
      ) : null}
    </div>
  );
};

export default ApprovedFormsPeriodHeader;
